import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  ItemCreateInput,
  ItineraryGroupedResponse,
  ItineraryItem,
  ItineraryItemUpdate,
  ItineraryListResponse,
  ManualItemInput,
} from '@wg/types';

export type { Logger };

export interface GetItineraryParams {
  grouped?: boolean;
  include_past?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
  status?: string;
  locale?: string;
}

export interface ItineraryApi {
  listItineraries(params?: { status?: string; locale?: string }): Promise<{
    itineraries: any[];
    total: number;
  }>;
  getItineraryItems(
    itineraryId: string,
    params?: GetItineraryParams,
  ): Promise<ItineraryListResponse>;
  getItineraryGrouped(
    itineraryId: string,
    params?: Omit<GetItineraryParams, 'grouped'>,
  ): Promise<ItineraryGroupedResponse>;
  addItem(itineraryId: string, item: ItemCreateInput): Promise<ItineraryItem>;
  addManualItem(
    itineraryId: string,
    item: ManualItemInput,
  ): Promise<ItineraryItem>;
  updateItineraryItem(
    itineraryId: string,
    itemId: string,
    updates: ItineraryItemUpdate,
  ): Promise<ItineraryItem>;
  removeItineraryItem(itineraryId: string, itemId: string): Promise<void>;
  moveItemToDate(
    itineraryId: string,
    itemId: string,
    newDate: string,
    newTime?: string,
  ): Promise<ItineraryItem>;
  getItemListing(
    itineraryId: string,
    itemId: string,
    forceRefresh?: boolean,
  ): Promise<any>;
}

const ITINERARIES_BASE = '/v1/itineraries';

const ITEM_TYPE_ICONS: Record<string, string> = {
  flight: 'airplane-outline',
  hotel: 'bed-outline',
  transport: 'car-outline',
  restaurant: 'restaurant-outline',
  activity: 'location-outline',
  note: 'document-text-outline',
  custom: 'star-outline',
};

const ITEM_TYPE_MAP: Record<string, string> = {
  flight: 'flight',
  hotel: 'hotel',
  transport: 'transport',
  restaurant: 'manual',
  activity: 'manual',
  note: 'manual',
  custom: 'manual',
};

/**
 * Normalize a backend item response into the frontend ItineraryItem shape.
 * Handles: location coords, type mapping, display hints, scheduled_time format.
 */
export function normalizeItineraryItem(
  raw: any,
  dayDate?: string,
): ItineraryItem {
  const dd = raw.display_data || {};
  const coords = dd.coordinates || {};

  const lat = raw.location_lat ?? dd.lat ?? coords.lat;
  const lng = raw.location_lng ?? dd.lng ?? coords.lng;

  const location =
    raw.location_name || raw.location_address || lat != null
      ? {
          name: raw.location?.name || raw.location_name || '',
          address: raw.location?.address || raw.location_address,
          ...(lat != null && lng != null
            ? { latitude: lat, longitude: lng }
            : {}),
        }
      : raw.location || undefined;

  const itemType = raw.item_type || 'activity';

  return {
    ...raw,
    type: ITEM_TYPE_MAP[itemType] || 'manual',
    scheduled_date: raw.scheduled_date || dayDate,
    scheduled_time: raw.scheduled_time || raw.start_time?.substring(0, 5),
    end_time: raw.end_time?.substring(0, 5) || raw.end_time,
    duration_minutes: raw.duration_minutes || dd.duration_minutes,
    location,
    display: raw.display || {
      icon: ITEM_TYPE_ICONS[itemType] || 'location-outline',
    },
    manual:
      raw.manual ||
      (['restaurant', 'activity', 'note'].includes(itemType)
        ? {
            category:
              itemType === 'restaurant'
                ? 'dining'
                : itemType === 'note'
                  ? 'other'
                  : 'activity',
          }
        : undefined),
    trip_id: raw.trip_id || raw.itinerary_id,
  };
}

/**
 * Map ManualItemInput (frontend form) to ItemCreateInput (backend schema).
 */
export function manualToCreateInput(item: ManualItemInput): ItemCreateInput {
  const categoryToItemType: Record<string, ItemCreateInput['item_type']> = {
    dining: 'restaurant',
    activity: 'activity',
    transport: 'transport',
    accommodation: 'hotel',
    flight: 'flight',
    note: 'note',
  };
  const itemType = categoryToItemType[item.category || ''] || 'activity';

  return {
    title: item.title,
    item_type: itemType,
    description: item.description,
    start_time: item.scheduled_time,
    end_time: item.end_time,
    all_day: item.all_day,
    location_name: item.location?.name,
    location_address: item.location?.address,
    notes: item.notes,
    provider_id: item.source_provider,
    provider_item_id: item.source_place_id,
    display_data: {
      ...(item.phone ? { phone: item.phone } : {}),
      ...(item.website ? { website: item.website } : {}),
      ...(item.location?.latitude ? { lat: item.location.latitude } : {}),
      ...(item.location?.longitude ? { lng: item.location.longitude } : {}),
      ...(item.duration_minutes
        ? { duration_minutes: item.duration_minutes }
        : {}),
    },
  };
}

export function createItineraryApi(
  http: AxiosInstance,
  logger: Logger,
): ItineraryApi {
  const api: ItineraryApi = {
    async listItineraries(params) {
      try {
        const response = await http.get<{ itineraries: any[]; total: number }>(
          ITINERARIES_BASE,
          { params },
        );
        logger.debug('[ItineraryAPI] Itineraries listed', {
          count: response.data.total,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to list itineraries', {
          error: error?.message,
        });
        throw error;
      }
    },

    async getItineraryItems(itineraryId, params) {
      try {
        const response = await http.get<ItineraryListResponse>(
          `${ITINERARIES_BASE}/${itineraryId}`,
          { params },
        );
        const data = response.data;
        data.items = data.items.map((item: any) =>
          normalizeItineraryItem(item),
        );
        logger.debug('[ItineraryAPI] Items retrieved', {
          itineraryId,
          count: data.total,
        });
        return data;
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to get items', {
          itineraryId,
          error: error?.message,
        });
        throw error;
      }
    },

    async getItineraryGrouped(itineraryId, params) {
      try {
        const response = await http.get<ItineraryGroupedResponse>(
          `${ITINERARIES_BASE}/${itineraryId}`,
          { params: { ...params, grouped: true } },
        );
        const data = response.data;
        data.days = data.days.map((day: any) => ({
          ...day,
          items: day.items.map((item: any) =>
            normalizeItineraryItem(item, day.date),
          ),
        }));
        if (data.unscheduled) {
          data.unscheduled = data.unscheduled.map((item: any) =>
            normalizeItineraryItem(item),
          );
        }
        logger.debug('[ItineraryAPI] Grouped items retrieved', {
          itineraryId,
          days: data.days.length,
          unscheduled: data.unscheduled?.length ?? 0,
          total: data.total_items,
        });
        return data;
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to get grouped items', {
          itineraryId,
          error: error?.message,
        });
        throw error;
      }
    },

    async addItem(itineraryId, item) {
      try {
        const response = await http.post<ItineraryItem>(
          `${ITINERARIES_BASE}/${itineraryId}/items`,
          item,
        );
        logger.info('[ItineraryAPI] Item added', {
          itineraryId,
          itemId: response.data.id,
          title: item.title,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to add item', {
          itineraryId,
          error: error?.message,
        });
        throw error;
      }
    },

    async addManualItem(itineraryId, item) {
      return api.addItem(itineraryId, manualToCreateInput(item));
    },

    async updateItineraryItem(itineraryId, itemId, updates) {
      try {
        const response = await http.put<ItineraryItem>(
          `${ITINERARIES_BASE}/${itineraryId}/items/${itemId}`,
          updates,
        );
        logger.info('[ItineraryAPI] Item updated', { itineraryId, itemId });
        return response.data;
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to update item', {
          itineraryId,
          itemId,
          error: error?.message,
        });
        throw error;
      }
    },

    async removeItineraryItem(itineraryId, itemId) {
      try {
        await http.delete(`${ITINERARIES_BASE}/${itineraryId}/items/${itemId}`);
        logger.info('[ItineraryAPI] Item removed', { itineraryId, itemId });
      } catch (error: any) {
        logger.error('[ItineraryAPI] Failed to remove item', {
          itineraryId,
          itemId,
          error: error?.message,
        });
        throw error;
      }
    },

    async moveItemToDate(itineraryId, itemId, newDate, newTime) {
      return api.updateItineraryItem(itineraryId, itemId, {
        scheduled_date: newDate,
        scheduled_time: newTime,
      });
    },

    async getItemListing(itineraryId, itemId, forceRefresh = false) {
      const params = forceRefresh ? '?force_refresh=true' : '';
      const { data } = await http.get(
        `${ITINERARIES_BASE}/${itineraryId}/items/${itemId}/listing${params}`,
      );
      return data;
    },
  };
  return api;
}

// ============================================================================
// Embed-flavored itinerary API
//
// The embed widget talks to the canonical functions-py paths
// (`/itineraries/v1/…`, NOT the legacy `/v1/itineraries` compat rewrite the
// RN/web factory above uses) and layers a richer per-item state machine
// (saved, pending_pay, confirmed, …) over the backend's coarser status enum.
// Embed-specific state (status, embedded_url, detection_patterns, guests,
// prices, image_url, etc.) round-trips through `display_data` so the wire
// shape stays standard functions-py while the iframe gets exactly what it
// stored.
//
// Auth comes from whatever client the caller injects — the embed passes its
// instance-scoped authed client (visitor session JWT, guest or
// authenticated). The backend's `get_current_user` dependency accepts both.
// ============================================================================

/** Wire shape for an itinerary row (subset the embed uses). */
export interface EmbedItineraryWire {
  id: string;
  user_id: string;
  title: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
  metadata?: Record<string, unknown> | null;
  /** Read-only share handle; null/absent until the owner mints a share link. */
  share_token?: string | null;
  created_at: string;
  updated_at: string;
}

/** Wire shape for an itinerary item row (functions-py response). */
export interface EmbedItineraryItemWire {
  id: string;
  itinerary_id: string;
  item_type: string;
  title: string;
  description?: string | null;
  location_name?: string | null;
  provider_id?: string | null;
  provider_item_id?: string | null;
  display_data?: Record<string, unknown> | null;
  status: string;
  priority?: string | null;
  scope?: string | null;
  added_by?: string | null;
  notes?: string | null;
  booking_flow?: string | null;
  created_at: string;
  updated_at?: string | null;
}

/** Visitor emphasis on an item, orthogonal to status. Absent = plain saved. */
export type EmbedItineraryItemPriority = 'must_do' | 'maybe';

/** Collaboration scope: the common plan vs one member's own pick. */
export type EmbedItineraryItemScope = 'group' | 'personal';

/** A collaborator's role on a shared itinerary. */
export type EmbedItineraryRole = 'owner' | 'editor' | 'viewer';

/** A minted collaboration invite link. */
export interface EmbedInvite {
  itinerary_id: string;
  token: string;
  role: 'editor' | 'viewer';
}

/** Anonymous preview of an invite (what a recipient sees before signing in). */
export interface EmbedInvitePreview {
  itinerary_id: string;
  title: string;
  role: 'editor' | 'viewer';
  invited_by_name?: string | null;
  valid: boolean;
}

/** Result of claiming an invite. */
export interface EmbedJoinResult {
  itinerary_id: string;
  role: EmbedItineraryRole;
  already_member: boolean;
}

/** One person on a shared trip. */
export interface EmbedMember {
  user_id: string;
  role: EmbedItineraryRole;
  name?: string | null;
  joined_at?: string | null;
  is_owner: boolean;
}

/** A trip the caller can access (owned or joined) — for the trip switcher. */
export interface EmbedTripSummary {
  itinerary_id: string;
  title: string;
  destination?: string | null;
  role: EmbedItineraryRole;
  is_owner: boolean;
  item_count: number;
  updated_at?: string | null;
}

/** Embed-side per-item state machine. */
export type EmbedItineraryItemStatus =
  | 'suggested'
  | 'saved'
  | 'pending_pay'
  | 'confirmed'
  | 'cancelled';

/**
 * Iframe-inspector rules carried from checkout-build. All-optional —
 * structurally compatible with the embed's CheckoutContext shape (a relaxed
 * variant of @wg/types DetectionPatterns).
 */
export interface EmbedDetectionPatterns {
  success_patterns?: string[];
  checkout_patterns?: string[];
  cancel_patterns?: string[];
  confirmation_code_params?: string[];
  /** Cooperating providers postMessage `{type: "...", ...}`; match here. */
  success_message_types?: string[];
}

/** Client-side itinerary item shape the embed renders + persists. */
export interface EmbedItineraryItem {
  /** Stable id; for server-backed items this is the UUID from the DB. */
  id: string;
  /** Display title — "Old Lahaina Lu‘au". */
  title: string;
  /** Catalog activity id (provider-prefixed when known). */
  activity_id?: string;
  /** Provider canonical id (lowercase). */
  provider?: string;
  /** Optional supplier id — for chokepoint principal binding. */
  external_supplier_id?: string;
  /** ISO date if scheduled (YYYY-MM-DD). */
  date?: string;
  /** Per-tier guest counts as visited at booking time. */
  guests?: { type: string; count: number; tier_id?: string | number }[];
  /** Per-person price when known. */
  unit_price_cents?: number;
  currency_code?: string;
  /** Optional hero image. */
  image_url?: string;
  /** Free-form short description for list rendering. */
  description?: string;
  /** Geo when known. */
  location_name?: string;
  status: EmbedItineraryItemStatus;
  /** Visitor emphasis (must_do | maybe). Absent = plain saved. Orthogonal
   *  to status — a confirmed booking can still be a must-do. */
  priority?: EmbedItineraryItemPriority;
  /** Collaboration scope: 'group' (the shared plan, default) or 'personal'
   *  (this member's own pick — visible to all, badged by added_by). */
  scope?: EmbedItineraryItemScope;
  /** auth.users id of whoever added the item (personal-pick badge). */
  added_by?: string;
  /** When `status='confirmed'`, the provider's confirmation code. */
  confirmation_code?: string;
  /** When pending_pay, the URL the cart should iframe. */
  embedded_url?: string;
  /** Iframe-inspector rules carried from checkout-build. */
  detection_patterns?: EmbedDetectionPatterns;
  /** Server-resolved checkout method (iframe | redirect | wg_internal). */
  booking_method?: string;
  /** Audit timestamps (ISO). */
  added_at: string;
  updated_at: string;
}

/** Map embed status → backend item status. */
const EMBED_TO_BACKEND_STATUS: Record<EmbedItineraryItemStatus, string> = {
  suggested: 'suggested',
  saved: 'suggested',
  pending_pay: 'pending_confirmation',
  confirmed: 'booked',
  cancelled: 'cancelled',
};

/** Map backend item status → embed status (best-effort fallback). */
const BACKEND_TO_EMBED_STATUS: Record<string, EmbedItineraryItemStatus> = {
  suggested: 'saved',
  pending_confirmation: 'pending_pay',
  confirmed: 'confirmed',
  booked: 'confirmed',
  cancelled: 'cancelled',
  completed: 'confirmed',
  rejected: 'cancelled',
};

/** Decode an embed item from a backend wire row. */
export function embedItemFromWire(
  row: EmbedItineraryItemWire,
): EmbedItineraryItem {
  const dd = (row.display_data || {}) as Record<string, unknown>;
  const embedStatus =
    (dd.embed_status as EmbedItineraryItemStatus | undefined) ||
    BACKEND_TO_EMBED_STATUS[row.status] ||
    'saved';
  return {
    id: row.id,
    title: row.title,
    activity_id:
      (dd.activity_id as string) || row.provider_item_id || undefined,
    provider: (dd.provider as string) || row.provider_id || undefined,
    external_supplier_id: dd.external_supplier_id as string | undefined,
    date: dd.date as string | undefined,
    guests: dd.guests as EmbedItineraryItem['guests'],
    unit_price_cents: dd.unit_price_cents as number | undefined,
    currency_code: dd.currency_code as string | undefined,
    image_url: dd.image_url as string | undefined,
    description: row.description ?? (dd.description as string | undefined),
    location_name:
      row.location_name ?? (dd.location_name as string | undefined),
    status: embedStatus,
    priority:
      (row.priority as EmbedItineraryItemPriority | undefined) ?? undefined,
    scope: (row.scope as EmbedItineraryItemScope | undefined) ?? undefined,
    added_by: (row.added_by as string | undefined) ?? undefined,
    confirmation_code: dd.confirmation_code as string | undefined,
    embedded_url: dd.embedded_url as string | undefined,
    detection_patterns:
      dd.detection_patterns as EmbedItineraryItem['detection_patterns'],
    booking_method:
      row.booking_flow ?? (dd.booking_method as string | undefined),
    added_at: row.created_at,
    updated_at: row.updated_at ?? row.created_at,
  };
}

/** Build the create-item payload for the backend from an embed item. */
export function embedItemToCreatePayload(
  item: Partial<EmbedItineraryItem> & { title: string },
): Record<string, unknown> {
  const status = item.status ?? 'saved';
  return {
    item_type: 'activity',
    title: item.title,
    description: item.description,
    location_name: item.location_name,
    provider_id: item.provider,
    provider_item_id: item.activity_id,
    booking_flow: mapBookingFlow(item.booking_method),
    status: EMBED_TO_BACKEND_STATUS[status],
    // Top-level column, not display_data — read paths filter on it. Omitted
    // from JSON when undefined, so the DB default (NULL / plain saved) holds.
    priority: item.priority,
    // Collaboration scope (omitted → backend default 'group'). added_by is
    // server-stamped, never sent by the client.
    scope: item.scope,
    display_data: {
      embed_status: status,
      activity_id: item.activity_id,
      provider: item.provider,
      external_supplier_id: item.external_supplier_id,
      date: item.date,
      guests: item.guests,
      unit_price_cents: item.unit_price_cents,
      currency_code: item.currency_code,
      image_url: item.image_url,
      embedded_url: item.embedded_url,
      detection_patterns: item.detection_patterns,
      booking_method: item.booking_method,
      confirmation_code: item.confirmation_code,
    },
  };
}

/** Build the update-item payload (PUT). Only includes provided fields. */
export function embedItemToUpdatePayload(
  current: EmbedItineraryItem,
  patch: Partial<EmbedItineraryItem>,
): Record<string, unknown> {
  const merged = { ...current, ...patch };
  const status = merged.status;
  return {
    title: patch.title,
    description: patch.description,
    location_name: patch.location_name,
    provider_id: patch.provider,
    provider_item_id: patch.activity_id,
    status: EMBED_TO_BACKEND_STATUS[status],
    // Only touch priority when the patch mentions it: value to set, explicit
    // null to clear (must_do/maybe → plain saved). Absent leaves it unchanged
    // (backend uses exclude_unset). undefined would be dropped from JSON, so
    // we send null explicitly for the clear case.
    ...('priority' in patch ? { priority: patch.priority ?? null } : {}),
    // Promote/demote between the group plan and a personal pick.
    ...('scope' in patch ? { scope: patch.scope } : {}),
    display_data: {
      embed_status: status,
      activity_id: merged.activity_id,
      provider: merged.provider,
      external_supplier_id: merged.external_supplier_id,
      date: merged.date,
      guests: merged.guests,
      unit_price_cents: merged.unit_price_cents,
      currency_code: merged.currency_code,
      image_url: merged.image_url,
      embedded_url: merged.embedded_url,
      detection_patterns: merged.detection_patterns,
      booking_method: merged.booking_method,
      confirmation_code: merged.confirmation_code,
    },
  };
}

/** Backend booking_flow column accepts redirect|iframe|api|custom. */
function mapBookingFlow(m?: string): string | undefined {
  if (!m) return undefined;
  if (m === 'wg_internal') return 'custom';
  if (m === 'iframe' || m === 'redirect' || m === 'api' || m === 'custom')
    return m;
  return 'custom';
}

export interface EmbedItineraryUpdatePatch {
  start_date?: string | null;
  end_date?: string | null;
  metadata?: Record<string, unknown>;
  title?: string;
}

export interface EmbedItineraryApi {
  listItineraries(): Promise<EmbedItineraryWire[]>;
  createItinerary(
    title: string,
    partnerInstanceToken?: string | null,
  ): Promise<EmbedItineraryWire>;
  getItineraryWithItems(itineraryId: string): Promise<{
    itinerary: EmbedItineraryWire;
    items: EmbedItineraryItemWire[];
  }>;
  createItineraryItem(
    itineraryId: string,
    payload: Record<string, unknown>,
  ): Promise<EmbedItineraryItemWire>;
  updateItineraryItem(
    itineraryId: string,
    itemId: string,
    payload: Record<string, unknown>,
  ): Promise<EmbedItineraryItemWire>;
  deleteItineraryItem(itineraryId: string, itemId: string): Promise<void>;
  updateItinerary(
    itineraryId: string,
    patch: EmbedItineraryUpdatePatch,
  ): Promise<EmbedItineraryWire>;
  /** Mint (or return) the read-only share token. rotate=true regenerates it,
   *  invalidating previously-distributed links. */
  shareItinerary(
    itineraryId: string,
    rotate?: boolean,
  ): Promise<{ itinerary_id: string; share_token: string | null }>;
  /** Revoke the share link (nulls share_token). */
  revokeShare(
    itineraryId: string,
  ): Promise<{ itinerary_id: string; share_token: string | null }>;
  /** Mint a collaboration invite link with a role (owner/editor only). */
  createInvite(
    itineraryId: string,
    role: 'editor' | 'viewer',
  ): Promise<EmbedInvite>;
  /** Revoke one invite link. */
  revokeInvite(itineraryId: string, token: string): Promise<void>;
  /** Anonymous preview of an invite token (for the join screen). */
  previewInvite(token: string): Promise<EmbedInvitePreview | null>;
  /** Claim an invite → become a member (REGISTERED users only server-side). */
  joinItinerary(token: string): Promise<EmbedJoinResult>;
  /** List everyone on the trip (owner + members). */
  listMembers(itineraryId: string): Promise<EmbedMember[]>;
  /** Remove a member (owner removes anyone; a member removes themselves). */
  removeMember(itineraryId: string, userId: string): Promise<void>;
  /** All trips the caller can access (owned + joined), for the switcher. */
  listMemberships(): Promise<EmbedTripSummary[]>;
}

export function createEmbedItineraryApi(
  http: AxiosInstance,
): EmbedItineraryApi {
  return {
    async listItineraries() {
      const { data } = await http.get<{ itineraries: EmbedItineraryWire[] }>(
        '/itineraries/v1',
        { params: { limit: 1 } },
      );
      return data?.itineraries ?? [];
    },

    async createItinerary(title, partnerInstanceToken) {
      const body: Record<string, unknown> = { title, locale: 'global' };
      // When set, the backend seeds one 'suggested' item from the partner's
      // primary published listing. Embed-only; RN omits this and gets the
      // legacy empty itinerary.
      if (partnerInstanceToken) {
        body.partner_instance_token = partnerInstanceToken;
      }
      const { data } = await http.post<EmbedItineraryWire>(
        '/itineraries/v1',
        body,
      );
      return data;
    },

    async getItineraryWithItems(itineraryId) {
      const { data } = await http.get<
        EmbedItineraryWire & { items: EmbedItineraryItemWire[] }
      >(`/itineraries/v1/${itineraryId}`);
      const { items, ...rest } = data;
      return { itinerary: rest as EmbedItineraryWire, items: items ?? [] };
    },

    async createItineraryItem(itineraryId, payload) {
      const { data } = await http.post<EmbedItineraryItemWire>(
        `/itineraries/v1/${itineraryId}/items`,
        payload,
      );
      return data;
    },

    async updateItineraryItem(itineraryId, itemId, payload) {
      const { data } = await http.put<EmbedItineraryItemWire>(
        `/itineraries/v1/${itineraryId}/items/${itemId}`,
        payload,
      );
      return data;
    },

    async deleteItineraryItem(itineraryId, itemId) {
      await http.delete(`/itineraries/v1/${itineraryId}/items/${itemId}`);
    },

    async updateItinerary(itineraryId, patch) {
      const { data } = await http.put<EmbedItineraryWire>(
        `/itineraries/v1/${itineraryId}`,
        patch,
      );
      return data;
    },

    async shareItinerary(itineraryId, rotate = false) {
      const { data } = await http.post<{
        itinerary_id: string;
        share_token: string | null;
      }>(`/itineraries/v1/${itineraryId}/share`, null, {
        params: rotate ? { rotate: true } : undefined,
      });
      return data;
    },

    async revokeShare(itineraryId) {
      const { data } = await http.delete<{
        itinerary_id: string;
        share_token: string | null;
      }>(`/itineraries/v1/${itineraryId}/share`);
      return data;
    },

    async createInvite(itineraryId, role) {
      const { data } = await http.post<EmbedInvite>(
        `/itineraries/v1/${itineraryId}/invites`,
        null,
        { params: { role } },
      );
      return data;
    },

    async revokeInvite(itineraryId, token) {
      await http.delete(
        `/itineraries/v1/${itineraryId}/invites/${encodeURIComponent(token)}`,
      );
    },

    async previewInvite(token) {
      try {
        const { data } = await http.get<EmbedInvitePreview>(
          `/itineraries/v1/invites/${encodeURIComponent(token)}`,
        );
        return data;
      } catch {
        // 404 unknown token → no preview.
        return null;
      }
    },

    async joinItinerary(token) {
      const { data } = await http.post<EmbedJoinResult>(
        `/itineraries/v1/join/${encodeURIComponent(token)}`,
      );
      return data;
    },

    async listMembers(itineraryId) {
      const { data } = await http.get<{ members: EmbedMember[] }>(
        `/itineraries/v1/${itineraryId}/members`,
      );
      return data?.members ?? [];
    },

    async removeMember(itineraryId, userId) {
      await http.delete(
        `/itineraries/v1/${itineraryId}/members/${encodeURIComponent(userId)}`,
      );
    },

    async listMemberships() {
      const { data } = await http.get<{ trips: EmbedTripSummary[] }>(
        '/itineraries/v1/memberships',
      );
      return data?.trips ?? [];
    },
  };
}
